import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule, SkipSelf } from '@angular/core';
import { AvcCoreDataBaseUrl } from '@garmin-avcloud/avcloud-fly-web-common/_shared';
import { FLY_WEB_COMMON_CONFIG } from '@garmin-avcloud/avcloud-fly-web-common/config';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams, HttpContext } from '@angular/common/http';

/**
 * Custom HttpParameterCodec
 * Workaround for https://github.com/angular/angular/issues/18261
 */
class CustomHttpParameterCodec {
  encodeKey(k) {
    return encodeURIComponent(k);
  }
  encodeValue(v) {
    return encodeURIComponent(v);
  }
  decodeKey(k) {
    return decodeURIComponent(k);
  }
  decodeValue(v) {
    return decodeURIComponent(v);
  }
}
const BASE_PATH = new InjectionToken('basePath');
const COLLECTION_FORMATS = {
  'csv': ',',
  'tsv': '   ',
  'ssv': ' ',
  'pipes': '|'
};
class AircraftWriteConfiguration {
  constructor(configurationParameters = {}) {
    this.apiKeys = configurationParameters.apiKeys;
    this.username = configurationParameters.username;
    this.password = configurationParameters.password;
    this.accessToken = configurationParameters.accessToken;
    this.basePath = configurationParameters.basePath;
    this.withCredentials = configurationParameters.withCredentials;
    this.encoder = configurationParameters.encoder;
    if (configurationParameters.encodeParam) {
      this.encodeParam = configurationParameters.encodeParam;
    } else {
      this.encodeParam = param => this.defaultEncodeParam(param);
    }
    if (configurationParameters.credentials) {
      this.credentials = configurationParameters.credentials;
    } else {
      this.credentials = {};
    }
    // init default bearerAuth credential
    if (!this.credentials['bearerAuth']) {
      this.credentials['bearerAuth'] = () => {
        return typeof this.accessToken === 'function' ? this.accessToken() : this.accessToken;
      };
    }
  }
  /**
   * Select the correct content-type to use for a request.
   * Uses {@link AircraftWriteConfiguration#isJsonMime} to determine the correct content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param contentTypes - the array of content types that are available for selection
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderContentType(contentTypes) {
    if (contentTypes.length === 0) {
      return undefined;
    }
    const type = contentTypes.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return contentTypes[0];
    }
    return type;
  }
  /**
   * Select the correct accept content-type to use for a request.
   * Uses {@link AircraftWriteConfiguration#isJsonMime} to determine the correct accept content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param accepts - the array of content types that are available for selection.
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderAccept(accepts) {
    if (accepts.length === 0) {
      return undefined;
    }
    const type = accepts.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return accepts[0];
    }
    return type;
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    const jsonMime = new RegExp('^(application\/json|[^;/ \t]+\/[^;/ \t]+[+]json)[ \t]*(;.*)?$', 'i');
    return mime !== null && (jsonMime.test(mime) || mime.toLowerCase() === 'application/json-patch+json');
  }
  lookupCredential(key) {
    const value = this.credentials[key];
    return typeof value === 'function' ? value() : value;
  }
  defaultEncodeParam(param) {
    // This implementation exists as fallback for missing configuration
    // and for backwards compatibility to older typescript-angular generator versions.
    // It only works for the 'simple' parameter style.
    // Date-handling only works for the 'date-time' format.
    // All other styles and Date-formats are probably handled incorrectly.
    //
    // But: if that's all you need (i.e.: the most common use-case): no need for customization!
    const value = param.dataFormat === 'date-time' && param.value instanceof Date ? param.value.toISOString() : param.value;
    return encodeURIComponent(String(value));
  }
}

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AircraftSyncWriteControllerService = /*#__PURE__*/(() => {
  class AircraftSyncWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    deleteAircraftSync(customerGuid, observe = 'body', reportProgress = false, options) {
      if (customerGuid === null || customerGuid === undefined) {
        throw new Error('Required parameter customerGuid was null or undefined when calling deleteAircraftSync.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (customerGuid !== undefined && customerGuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerGuid, 'customerGuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/sync`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AircraftSyncWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftSyncWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AircraftSyncWriteControllerService,
        factory: AircraftSyncWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AircraftSyncWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AircraftSystemWriteControllerService = /*#__PURE__*/(() => {
  class AircraftSystemWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    archiveAircraftSystem(uuid, refreshConnectedCockpitRegistration, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling archiveAircraftSystem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (refreshConnectedCockpitRegistration !== undefined && refreshConnectedCockpitRegistration !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, refreshConnectedCockpitRegistration, 'refreshConnectedCockpitRegistration');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    archiveAircraftSystemDevice(systemUuid, deviceUuid, observe = 'body', reportProgress = false, options) {
      if (systemUuid === null || systemUuid === undefined) {
        throw new Error('Required parameter systemUuid was null or undefined when calling archiveAircraftSystemDevice.');
      }
      if (deviceUuid === null || deviceUuid === undefined) {
        throw new Error('Required parameter deviceUuid was null or undefined when calling archiveAircraftSystemDevice.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/${this.configuration.encodeParam({
        name: "systemUuid",
        value: systemUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/devices/${this.configuration.encodeParam({
        name: "deviceUuid",
        value: deviceUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createAircraftSystem(aircraftSystem, includeMasterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (aircraftSystem === null || aircraftSystem === undefined) {
        throw new Error('Required parameter aircraftSystem was null or undefined when calling createAircraftSystem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (includeMasterAircraftUuid !== undefined && includeMasterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeMasterAircraftUuid, 'includeMasterAircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftSystem,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createAircraftSystemDevice(uuid, aircraftDevice, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling createAircraftSystemDevice.');
      }
      if (aircraftDevice === null || aircraftDevice === undefined) {
        throw new Error('Required parameter aircraftDevice was null or undefined when calling createAircraftSystemDevice.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/devices`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftDevice,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createAircraftSystemWithDevices(aircraftSystemSummary, includeMasterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (aircraftSystemSummary === null || aircraftSystemSummary === undefined) {
        throw new Error('Required parameter aircraftSystemSummary was null or undefined when calling createAircraftSystemWithDevices.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (includeMasterAircraftUuid !== undefined && includeMasterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeMasterAircraftUuid, 'includeMasterAircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/with-devices`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftSystemSummary,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    hardDeleteAircraftSystem(uuid, refreshConnectedCockpitRegistration, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling hardDeleteAircraftSystem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (refreshConnectedCockpitRegistration !== undefined && refreshConnectedCockpitRegistration !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, refreshConnectedCockpitRegistration, 'refreshConnectedCockpitRegistration');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/hard`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    hardDeleteAircraftSystemDevice(systemUuid, deviceUuid, observe = 'body', reportProgress = false, options) {
      if (systemUuid === null || systemUuid === undefined) {
        throw new Error('Required parameter systemUuid was null or undefined when calling hardDeleteAircraftSystemDevice.');
      }
      if (deviceUuid === null || deviceUuid === undefined) {
        throw new Error('Required parameter deviceUuid was null or undefined when calling hardDeleteAircraftSystemDevice.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/${this.configuration.encodeParam({
        name: "systemUuid",
        value: systemUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/devices/${this.configuration.encodeParam({
        name: "deviceUuid",
        value: deviceUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/hard`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateAircraftSystem(uuid, aircraftSystem, includeMasterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateAircraftSystem.');
      }
      if (aircraftSystem === null || aircraftSystem === undefined) {
        throw new Error('Required parameter aircraftSystem was null or undefined when calling updateAircraftSystem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (includeMasterAircraftUuid !== undefined && includeMasterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeMasterAircraftUuid, 'includeMasterAircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftSystem,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateAircraftSystemDevice(systemUuid, deviceUuid, aircraftDevice, observe = 'body', reportProgress = false, options) {
      if (systemUuid === null || systemUuid === undefined) {
        throw new Error('Required parameter systemUuid was null or undefined when calling updateAircraftSystemDevice.');
      }
      if (deviceUuid === null || deviceUuid === undefined) {
        throw new Error('Required parameter deviceUuid was null or undefined when calling updateAircraftSystemDevice.');
      }
      if (aircraftDevice === null || aircraftDevice === undefined) {
        throw new Error('Required parameter aircraftDevice was null or undefined when calling updateAircraftSystemDevice.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/${this.configuration.encodeParam({
        name: "systemUuid",
        value: systemUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/devices/${this.configuration.encodeParam({
        name: "deviceUuid",
        value: deviceUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftDevice,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AircraftSystemWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftSystemWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AircraftSystemWriteControllerService,
        factory: AircraftSystemWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AircraftSystemWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AircraftWriteControllerService = /*#__PURE__*/(() => {
  class AircraftWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createAircraft(aircraftRequest, observe = 'body', reportProgress = false, options) {
      if (aircraftRequest === null || aircraftRequest === undefined) {
        throw new Error('Required parameter aircraftRequest was null or undefined when calling createAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAircraft(aircraftUuid, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling deleteAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteAircraftImage(aircraftUuid, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling deleteAircraftImage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/image`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    promote(aircraftUuid, promoteRequest, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling promote.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/promotion/promote`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: promoteRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    promoteAircraft(aircraftUuid, aircraftValidationRequest, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling promoteAircraft.');
      }
      if (aircraftValidationRequest === null || aircraftValidationRequest === undefined) {
        throw new Error('Required parameter aircraftValidationRequest was null or undefined when calling promoteAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/promote`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftValidationRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateAircraft(aircraftUuid, aircraftRequest, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling updateAircraft.');
      }
      if (aircraftRequest === null || aircraftRequest === undefined) {
        throw new Error('Required parameter aircraftRequest was null or undefined when calling updateAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateAircraftWakeupSettings(aircraftUuid, aircraftWakeupSettings, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling updateAircraftWakeupSettings.');
      }
      if (aircraftWakeupSettings === null || aircraftWakeupSettings === undefined) {
        throw new Error('Required parameter aircraftWakeupSettings was null or undefined when calling updateAircraftWakeupSettings.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/wakeup-settings`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftWakeupSettings,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    uploadAircraftImage(aircraftUuid, uploadAircraftImageRequest, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling uploadAircraftImage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/image`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: uploadAircraftImageRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AircraftWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AircraftWriteControllerService,
        factory: AircraftWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AircraftWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CategoriesWriteControllerService = /*#__PURE__*/(() => {
  class CategoriesWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    deleteCategory(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteCategory.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/categories/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    postCategories(category, observe = 'body', reportProgress = false, options) {
      if (category === null || category === undefined) {
        throw new Error('Required parameter category was null or undefined when calling postCategories.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/categories`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: category,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    putCategory(id, category, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling putCategory.');
      }
      if (category === null || category === undefined) {
        throw new Error('Required parameter category was null or undefined when calling putCategory.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/categories/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: category,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CategoriesWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CategoriesWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CategoriesWriteControllerService,
        factory: CategoriesWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CategoriesWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ConnectedCockpitRegistrationWriteControllerService = /*#__PURE__*/(() => {
  class ConnectedCockpitRegistrationWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createConnectedCockpitRegistration(masterAircraftUuid, duration, observe = 'body', reportProgress = false, options) {
      if (masterAircraftUuid === null || masterAircraftUuid === undefined) {
        throw new Error('Required parameter masterAircraftUuid was null or undefined when calling createConnectedCockpitRegistration.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (masterAircraftUuid !== undefined && masterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, masterAircraftUuid, 'masterAircraftUuid');
      }
      if (duration !== undefined && duration !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, duration, 'duration');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/connected-cockpit-registrations`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteConnectedCockpitRegistration(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteConnectedCockpitRegistration.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/connected-cockpit-registrations/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteConnectedCockpitRegistrationUnredeemed(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteConnectedCockpitRegistrationUnredeemed.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/connected-cockpit-registrations/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/unredeemed`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    generateNonce(nonceRequestDevice, observe = 'body', reportProgress = false, options) {
      if (nonceRequestDevice === null || nonceRequestDevice === undefined) {
        throw new Error('Required parameter nonceRequestDevice was null or undefined when calling generateNonce.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/connected-cockpit-registrations/nonce`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: nonceRequestDevice,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    refresh(masterAircraftUuid, duration, observe = 'body', reportProgress = false, options) {
      if (masterAircraftUuid === null || masterAircraftUuid === undefined) {
        throw new Error('Required parameter masterAircraftUuid was null or undefined when calling refresh.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (masterAircraftUuid !== undefined && masterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, masterAircraftUuid, 'masterAircraftUuid');
      }
      if (duration !== undefined && duration !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, duration, 'duration');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/connected-cockpit-registrations/refresh`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateConnectedCockpitRegistration(uuid, connectedCockpitRegistration, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateConnectedCockpitRegistration.');
      }
      if (connectedCockpitRegistration === null || connectedCockpitRegistration === undefined) {
        throw new Error('Required parameter connectedCockpitRegistration was null or undefined when calling updateConnectedCockpitRegistration.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/connected-cockpit-registrations/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: connectedCockpitRegistration,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    validate(hash, observe = 'body', reportProgress = false, options) {
      if (hash === null || hash === undefined) {
        throw new Error('Required parameter hash was null or undefined when calling validate.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (hash !== undefined && hash !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, hash, 'hash');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/connected-cockpit-registrations/validate`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ConnectedCockpitRegistrationWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ConnectedCockpitRegistrationWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ConnectedCockpitRegistrationWriteControllerService,
        factory: ConnectedCockpitRegistrationWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ConnectedCockpitRegistrationWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CustomerAircraftDeviceWriteControllerService = /*#__PURE__*/(() => {
  class CustomerAircraftDeviceWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createCustomerAircraftDevice(customerAircraftDevice, observe = 'body', reportProgress = false, options) {
      if (customerAircraftDevice === null || customerAircraftDevice === undefined) {
        throw new Error('Required parameter customerAircraftDevice was null or undefined when calling createCustomerAircraftDevice.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft-devices`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: customerAircraftDevice,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteCustomerAircraftDevice(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteCustomerAircraftDevice.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft-devices/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteCustomerAircraftDeviceHard(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteCustomerAircraftDeviceHard.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft-devices/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}/hard`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateCustomerAircraftDevice(id, customerAircraftDevice, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateCustomerAircraftDevice.');
      }
      if (customerAircraftDevice === null || customerAircraftDevice === undefined) {
        throw new Error('Required parameter customerAircraftDevice was null or undefined when calling updateCustomerAircraftDevice.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft-devices/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: customerAircraftDevice,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CustomerAircraftDeviceWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CustomerAircraftDeviceWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CustomerAircraftDeviceWriteControllerService,
        factory: CustomerAircraftDeviceWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CustomerAircraftDeviceWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CustomerAircraftWriteControllerService = /*#__PURE__*/(() => {
  class CustomerAircraftWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createCustomerAircraft(customerAircraft, observe = 'body', reportProgress = false, options) {
      if (customerAircraft === null || customerAircraft === undefined) {
        throw new Error('Required parameter customerAircraft was null or undefined when calling createCustomerAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: customerAircraft,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteCustomerAircraft(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteCustomerAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteCustomerAircraftHard(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteCustomerAircraftHard.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/hard`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateCustomerAircraft(uuid, customerAircraft, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateCustomerAircraft.');
      }
      if (customerAircraft === null || customerAircraft === undefined) {
        throw new Error('Required parameter customerAircraft was null or undefined when calling updateCustomerAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: customerAircraft,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CustomerAircraftWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CustomerAircraftWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CustomerAircraftWriteControllerService,
        factory: CustomerAircraftWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CustomerAircraftWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let DataPrivacyWriteControllerService = /*#__PURE__*/(() => {
  class DataPrivacyWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    deleteDataPrivacyCustomer(customerGuid, observe = 'body', reportProgress = false, options) {
      if (customerGuid === null || customerGuid === undefined) {
        throw new Error('Required parameter customerGuid was null or undefined when calling deleteDataPrivacyCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/data-privacy/${this.configuration.encodeParam({
        name: "customerGuid",
        value: customerGuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/delete`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    purgeCustomer(customerGuid, observe = 'body', reportProgress = false, options) {
      if (customerGuid === null || customerGuid === undefined) {
        throw new Error('Required parameter customerGuid was null or undefined when calling purgeCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/data-privacy/${this.configuration.encodeParam({
        name: "customerGuid",
        value: customerGuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/purge`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function DataPrivacyWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DataPrivacyWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DataPrivacyWriteControllerService,
        factory: DataPrivacyWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DataPrivacyWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let DefaultAircraftImageWriteControllerService = /*#__PURE__*/(() => {
  class DefaultAircraftImageWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    deleteDefaultAircraftImage(modelId, observe = 'body', reportProgress = false, options) {
      if (modelId === null || modelId === undefined) {
        throw new Error('Required parameter modelId was null or undefined when calling deleteDefaultAircraftImage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-images/default/${this.configuration.encodeParam({
        name: "modelId",
        value: modelId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    postDefaultAircraftImages(defaultAircraftImage, observe = 'body', reportProgress = false, options) {
      if (defaultAircraftImage === null || defaultAircraftImage === undefined) {
        throw new Error('Required parameter defaultAircraftImage was null or undefined when calling postDefaultAircraftImages.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-images/default`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: defaultAircraftImage,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    putDefaultAircraftImage(modelId, defaultAircraftImage, observe = 'body', reportProgress = false, options) {
      if (modelId === null || modelId === undefined) {
        throw new Error('Required parameter modelId was null or undefined when calling putDefaultAircraftImage.');
      }
      if (defaultAircraftImage === null || defaultAircraftImage === undefined) {
        throw new Error('Required parameter defaultAircraftImage was null or undefined when calling putDefaultAircraftImage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-images/default/${this.configuration.encodeParam({
        name: "modelId",
        value: modelId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: defaultAircraftImage,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function DefaultAircraftImageWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DefaultAircraftImageWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DefaultAircraftImageWriteControllerService,
        factory: DefaultAircraftImageWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DefaultAircraftImageWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EngineTypeWriteControllerService = /*#__PURE__*/(() => {
  class EngineTypeWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createEngineType(engineType, observe = 'body', reportProgress = false, options) {
      if (engineType === null || engineType === undefined) {
        throw new Error('Required parameter engineType was null or undefined when calling createEngineType.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/engine-types`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: engineType,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteEngineType(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteEngineType.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/engine-types/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateEngineType(id, engineType, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateEngineType.');
      }
      if (engineType === null || engineType === undefined) {
        throw new Error('Required parameter engineType was null or undefined when calling updateEngineType.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/engine-types/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: engineType,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EngineTypeWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EngineTypeWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EngineTypeWriteControllerService,
        factory: EngineTypeWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EngineTypeWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FirewallAllowListWriteControllerService = /*#__PURE__*/(() => {
  class FirewallAllowListWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createFirewallAllowList(firewallAllowList, observe = 'body', reportProgress = false, options) {
      if (firewallAllowList === null || firewallAllowList === undefined) {
        throw new Error('Required parameter firewallAllowList was null or undefined when calling createFirewallAllowList.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/allow-lists`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: firewallAllowList,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteFirewallAllowList(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteFirewallAllowList.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/allow-lists/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateFirewallAllowList(uuid, firewallAllowList, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateFirewallAllowList.');
      }
      if (firewallAllowList === null || firewallAllowList === undefined) {
        throw new Error('Required parameter firewallAllowList was null or undefined when calling updateFirewallAllowList.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/allow-lists/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: firewallAllowList,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FirewallAllowListWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FirewallAllowListWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FirewallAllowListWriteControllerService,
        factory: FirewallAllowListWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FirewallAllowListWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FirewallFeatureWriteControllerService = /*#__PURE__*/(() => {
  class FirewallFeatureWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createFirewallFeature(firewallFeature, observe = 'body', reportProgress = false, options) {
      if (firewallFeature === null || firewallFeature === undefined) {
        throw new Error('Required parameter firewallFeature was null or undefined when calling createFirewallFeature.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/features`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: firewallFeature,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteFirewallFeature(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteFirewallFeature.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/features/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateFirewallFeature(uuid, firewallFeature, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateFirewallFeature.');
      }
      if (firewallFeature === null || firewallFeature === undefined) {
        throw new Error('Required parameter firewallFeature was null or undefined when calling updateFirewallFeature.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/features/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: firewallFeature,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FirewallFeatureWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FirewallFeatureWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FirewallFeatureWriteControllerService,
        factory: FirewallFeatureWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FirewallFeatureWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FirewallServiceWriteControllerService = /*#__PURE__*/(() => {
  class FirewallServiceWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createFirewallService(firewallService, observe = 'body', reportProgress = false, options) {
      if (firewallService === null || firewallService === undefined) {
        throw new Error('Required parameter firewallService was null or undefined when calling createFirewallService.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/services`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: firewallService,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteFirewallService(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteFirewallService.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/services/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateFirewallService(uuid, firewallService, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateFirewallService.');
      }
      if (firewallService === null || firewallService === undefined) {
        throw new Error('Required parameter firewallService was null or undefined when calling updateFirewallService.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/services/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: firewallService,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FirewallServiceWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FirewallServiceWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FirewallServiceWriteControllerService,
        factory: FirewallServiceWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FirewallServiceWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FixedDeviceWriteControllerService = /*#__PURE__*/(() => {
  class FixedDeviceWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createFixedDevice(fixedDevice, observe = 'body', reportProgress = false, options) {
      if (fixedDevice === null || fixedDevice === undefined) {
        throw new Error('Required parameter fixedDevice was null or undefined when calling createFixedDevice.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/fixed-devices`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: fixedDevice,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteFixedDevice(uuid, refreshConnectedCockpitRegistration, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteFixedDevice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (refreshConnectedCockpitRegistration !== undefined && refreshConnectedCockpitRegistration !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, refreshConnectedCockpitRegistration, 'refreshConnectedCockpitRegistration');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/fixed-devices/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    hardDeleteFixedDevice(uuid, refreshConnectedCockpitRegistration, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling hardDeleteFixedDevice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (refreshConnectedCockpitRegistration !== undefined && refreshConnectedCockpitRegistration !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, refreshConnectedCockpitRegistration, 'refreshConnectedCockpitRegistration');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/fixed-devices/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/hard`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateFixedDevice(uuid, fixedDevice, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateFixedDevice.');
      }
      if (fixedDevice === null || fixedDevice === undefined) {
        throw new Error('Required parameter fixedDevice was null or undefined when calling updateFixedDevice.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/fixed-devices/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: fixedDevice,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FixedDeviceWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FixedDeviceWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FixedDeviceWriteControllerService,
        factory: FixedDeviceWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FixedDeviceWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let LegacyModelWriteControllerService = /*#__PURE__*/(() => {
  class LegacyModelWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createLegacyModel(legacyModel, observe = 'body', reportProgress = false, options) {
      if (legacyModel === null || legacyModel === undefined) {
        throw new Error('Required parameter legacyModel was null or undefined when calling createLegacyModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/legacy-models`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: legacyModel,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteLegacyModel(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteLegacyModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/legacy-models/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateLegacyModel(id, legacyModel, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateLegacyModel.');
      }
      if (legacyModel === null || legacyModel === undefined) {
        throw new Error('Required parameter legacyModel was null or undefined when calling updateLegacyModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/legacy-models/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: legacyModel,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function LegacyModelWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LegacyModelWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LegacyModelWriteControllerService,
        factory: LegacyModelWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LegacyModelWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MakeWriteControllerService = /*#__PURE__*/(() => {
  class MakeWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createMake(make, observe = 'body', reportProgress = false, options) {
      if (make === null || make === undefined) {
        throw new Error('Required parameter make was null or undefined when calling createMake.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/makes`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: make,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteMake(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteMake.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/makes/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateMake(id, make, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateMake.');
      }
      if (make === null || make === undefined) {
        throw new Error('Required parameter make was null or undefined when calling updateMake.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/makes/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: make,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MakeWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MakeWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MakeWriteControllerService,
        factory: MakeWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MakeWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftCustomerRoleWriteControllerService = /*#__PURE__*/(() => {
  class MasterAircraftCustomerRoleWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    assignOwner(masterAircraftUuid, customerGuid, organizationUuid, observe = 'body', reportProgress = false, options) {
      if (masterAircraftUuid === null || masterAircraftUuid === undefined) {
        throw new Error('Required parameter masterAircraftUuid was null or undefined when calling assignOwner.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (customerGuid !== undefined && customerGuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerGuid, 'customerGuid');
      }
      if (organizationUuid !== undefined && organizationUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, organizationUuid, 'organizationUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft-customer-roles/master-aircraft/${this.configuration.encodeParam({
        name: "masterAircraftUuid",
        value: masterAircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/assign-owner`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createMasterAircraftCustomerRole(masterAircraftCustomerRole, observe = 'body', reportProgress = false, options) {
      if (masterAircraftCustomerRole === null || masterAircraftCustomerRole === undefined) {
        throw new Error('Required parameter masterAircraftCustomerRole was null or undefined when calling createMasterAircraftCustomerRole.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft-customer-roles`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraftCustomerRole,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteMasterAircraftCustomerRole(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteMasterAircraftCustomerRole.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft-customer-roles/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteMasterAircraftCustomerRoleHard(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteMasterAircraftCustomerRoleHard.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft-customer-roles/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/hard`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateMasterAircraftCustomerRole(uuid, masterAircraftCustomerRole, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateMasterAircraftCustomerRole.');
      }
      if (masterAircraftCustomerRole === null || masterAircraftCustomerRole === undefined) {
        throw new Error('Required parameter masterAircraftCustomerRole was null or undefined when calling updateMasterAircraftCustomerRole.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft-customer-roles/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraftCustomerRole,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftCustomerRoleWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftCustomerRoleWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftCustomerRoleWriteControllerService,
        factory: MasterAircraftCustomerRoleWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftCustomerRoleWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftInfoWriteControllerService = /*#__PURE__*/(() => {
  class MasterAircraftInfoWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createMasterAircraftInfo(masterAircraftInfo, observe = 'body', reportProgress = false, options) {
      if (masterAircraftInfo === null || masterAircraftInfo === undefined) {
        throw new Error('Required parameter masterAircraftInfo was null or undefined when calling createMasterAircraftInfo.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/info`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraftInfo,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteMasterAircraftInfo(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteMasterAircraftInfo.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/info/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateMasterAircraftInfo(uuid, masterAircraftInfo, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateMasterAircraftInfo.');
      }
      if (masterAircraftInfo === null || masterAircraftInfo === undefined) {
        throw new Error('Required parameter masterAircraftInfo was null or undefined when calling updateMasterAircraftInfo.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/info/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraftInfo,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftInfoWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftInfoWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftInfoWriteControllerService,
        factory: MasterAircraftInfoWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftInfoWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftSystemWriteControllerService = /*#__PURE__*/(() => {
  class MasterAircraftSystemWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createMasterAircraftSystem(masterAircraftSystem, observe = 'body', reportProgress = false, options) {
      if (masterAircraftSystem === null || masterAircraftSystem === undefined) {
        throw new Error('Required parameter masterAircraftSystem was null or undefined when calling createMasterAircraftSystem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/systems`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraftSystem,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteMasterAircraftSystem(uuid, refreshConnectedCockpitRegistration, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteMasterAircraftSystem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (refreshConnectedCockpitRegistration !== undefined && refreshConnectedCockpitRegistration !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, refreshConnectedCockpitRegistration, 'refreshConnectedCockpitRegistration');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/systems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    hardDeleteMasterAircraftSystem(uuid, refreshConnectedCockpitRegistration, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling hardDeleteMasterAircraftSystem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (refreshConnectedCockpitRegistration !== undefined && refreshConnectedCockpitRegistration !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, refreshConnectedCockpitRegistration, 'refreshConnectedCockpitRegistration');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/systems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/hard`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateMasterAircraftSystem(uuid, masterAircraftSystem, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateMasterAircraftSystem.');
      }
      if (masterAircraftSystem === null || masterAircraftSystem === undefined) {
        throw new Error('Required parameter masterAircraftSystem was null or undefined when calling updateMasterAircraftSystem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/systems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraftSystem,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftSystemWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftSystemWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftSystemWriteControllerService,
        factory: MasterAircraftSystemWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftSystemWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftTailNumberWriteControllerService = /*#__PURE__*/(() => {
  class MasterAircraftTailNumberWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createTailNumber(masterAircraftTailNumber, observe = 'body', reportProgress = false, options) {
      if (masterAircraftTailNumber === null || masterAircraftTailNumber === undefined) {
        throw new Error('Required parameter masterAircraftTailNumber was null or undefined when calling createTailNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/tail-numbers`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraftTailNumber,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteTailNumber(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteTailNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/tail-numbers/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteTailNumberHard(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteTailNumberHard.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/tail-numbers/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/hard`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateTailNumber(uuid, masterAircraftTailNumber, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateTailNumber.');
      }
      if (masterAircraftTailNumber === null || masterAircraftTailNumber === undefined) {
        throw new Error('Required parameter masterAircraftTailNumber was null or undefined when calling updateTailNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/tail-numbers/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraftTailNumber,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftTailNumberWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftTailNumberWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftTailNumberWriteControllerService,
        factory: MasterAircraftTailNumberWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftTailNumberWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftWakeupSettingsWriteControllerService = /*#__PURE__*/(() => {
  class MasterAircraftWakeupSettingsWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createMasterAircraftWakeupSettings(masterAircraftWakeupSettings, observe = 'body', reportProgress = false, options) {
      if (masterAircraftWakeupSettings === null || masterAircraftWakeupSettings === undefined) {
        throw new Error('Required parameter masterAircraftWakeupSettings was null or undefined when calling createMasterAircraftWakeupSettings.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/wakeup-settings`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraftWakeupSettings,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteMasterAircraftWakeupSettings(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteMasterAircraftWakeupSettings.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/wakeup-settings/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateMasterAircraftWakeupSettings(uuid, masterAircraftWakeupSettings, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateMasterAircraftWakeupSettings.');
      }
      if (masterAircraftWakeupSettings === null || masterAircraftWakeupSettings === undefined) {
        throw new Error('Required parameter masterAircraftWakeupSettings was null or undefined when calling updateMasterAircraftWakeupSettings.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/wakeup-settings/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraftWakeupSettings,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftWakeupSettingsWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftWakeupSettingsWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftWakeupSettingsWriteControllerService,
        factory: MasterAircraftWakeupSettingsWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftWakeupSettingsWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftWriteControllerService = /*#__PURE__*/(() => {
  class MasterAircraftWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createMasterAircraft(masterAircraft, observe = 'body', reportProgress = false, options) {
      if (masterAircraft === null || masterAircraft === undefined) {
        throw new Error('Required parameter masterAircraft was null or undefined when calling createMasterAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraft,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteMasterAircraft(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteMasterAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteMasterAircraftHard(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteMasterAircraftHard.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/hard`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateMasterAircraft(uuid, masterAircraft, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateMasterAircraft.');
      }
      if (masterAircraft === null || masterAircraft === undefined) {
        throw new Error('Required parameter masterAircraft was null or undefined when calling updateMasterAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraft,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftWriteControllerService,
        factory: MasterAircraftWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ModelWriteControllerService = /*#__PURE__*/(() => {
  class ModelWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createModel(model, observe = 'body', reportProgress = false, options) {
      if (model === null || model === undefined) {
        throw new Error('Required parameter model was null or undefined when calling createModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/models`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: model,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteModel(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/models/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateModel(id, model, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateModel.');
      }
      if (model === null || model === undefined) {
        throw new Error('Required parameter model was null or undefined when calling updateModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/models/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: model,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ModelWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ModelWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ModelWriteControllerService,
        factory: ModelWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ModelWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let OemAircraftModelWriteControllerService = /*#__PURE__*/(() => {
  class OemAircraftModelWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createOemAircraftModel(oemAircraftModel, observe = 'body', reportProgress = false, options) {
      if (oemAircraftModel === null || oemAircraftModel === undefined) {
        throw new Error('Required parameter oemAircraftModel was null or undefined when calling createOemAircraftModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oem-aircraft-models`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: oemAircraftModel,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteOemAircraftModel(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteOemAircraftModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oem-aircraft-models/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateOemAircraftModel(uuid, oemAircraftModel, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateOemAircraftModel.');
      }
      if (oemAircraftModel === null || oemAircraftModel === undefined) {
        throw new Error('Required parameter oemAircraftModel was null or undefined when calling updateOemAircraftModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oem-aircraft-models/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: oemAircraftModel,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function OemAircraftModelWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OemAircraftModelWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OemAircraftModelWriteControllerService,
        factory: OemAircraftModelWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OemAircraftModelWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let OemUserWriteControllerService = /*#__PURE__*/(() => {
  class OemUserWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createOemUser(oemUser, observe = 'body', reportProgress = false, options) {
      if (oemUser === null || oemUser === undefined) {
        throw new Error('Required parameter oemUser was null or undefined when calling createOemUser.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oem-users`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: oemUser,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteOemUser(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteOemUser.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oem-users/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateOemUser(uuid, oemUser, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateOemUser.');
      }
      if (oemUser === null || oemUser === undefined) {
        throw new Error('Required parameter oemUser was null or undefined when calling updateOemUser.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oem-users/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: oemUser,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function OemUserWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OemUserWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OemUserWriteControllerService,
        factory: OemUserWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OemUserWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let OemWriteControllerService = /*#__PURE__*/(() => {
  class OemWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createOem(oem, observe = 'body', reportProgress = false, options) {
      if (oem === null || oem === undefined) {
        throw new Error('Required parameter oem was null or undefined when calling createOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oems`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: oem,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteOem(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    hardDeleteOem(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling hardDeleteOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/hard`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateOem(uuid, oem, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateOem.');
      }
      if (oem === null || oem === undefined) {
        throw new Error('Required parameter oem was null or undefined when calling updateOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: oem,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function OemWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OemWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OemWriteControllerService,
        factory: OemWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OemWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let RoleInviteWriteControllerService = /*#__PURE__*/(() => {
  class RoleInviteWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    acceptRoleInvite(uuid, customerGuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling acceptRoleInvite.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (customerGuid !== undefined && customerGuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerGuid, 'customerGuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/role-invites/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/accept`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    createRoleInvite(roleInvite, autoAccept, observe = 'body', reportProgress = false, options) {
      if (roleInvite === null || roleInvite === undefined) {
        throw new Error('Required parameter roleInvite was null or undefined when calling createRoleInvite.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (autoAccept !== undefined && autoAccept !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, autoAccept, 'autoAccept');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/role-invites`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: roleInvite,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteRoleInvite(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteRoleInvite.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/role-invites/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteRoleInviteHard(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling deleteRoleInviteHard.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/role-invites/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/hard`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateRoleInvite(uuid, roleInvite, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling updateRoleInvite.');
      }
      if (roleInvite === null || roleInvite === undefined) {
        throw new Error('Required parameter roleInvite was null or undefined when calling updateRoleInvite.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/role-invites/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: roleInvite,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function RoleInviteWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RoleInviteWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: RoleInviteWriteControllerService,
        factory: RoleInviteWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return RoleInviteWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SubcategoryWriteControllerService = /*#__PURE__*/(() => {
  class SubcategoryWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createSubcategory(subcategory, observe = 'body', reportProgress = false, options) {
      if (subcategory === null || subcategory === undefined) {
        throw new Error('Required parameter subcategory was null or undefined when calling createSubcategory.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/subcategories`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: subcategory,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteSubcategory(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling deleteSubcategory.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/subcategories/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateSubcategory(id, subcategory, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling updateSubcategory.');
      }
      if (subcategory === null || subcategory === undefined) {
        throw new Error('Required parameter subcategory was null or undefined when calling updateSubcategory.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/subcategories/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: subcategory,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SubcategoryWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SubcategoryWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SubcategoryWriteControllerService,
        factory: SubcategoryWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SubcategoryWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let WakeupTypeWriteControllerService = /*#__PURE__*/(() => {
  class WakeupTypeWriteControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementWriteService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftWriteConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    createWakeupType(wakeupType, observe = 'body', reportProgress = false, options) {
      if (wakeupType === null || wakeupType === undefined) {
        throw new Error('Required parameter wakeupType was null or undefined when calling createWakeupType.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/wakeup-types`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wakeupType,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    deleteWakeupType(type, observe = 'body', reportProgress = false, options) {
      if (type === null || type === undefined) {
        throw new Error('Required parameter type was null or undefined when calling deleteWakeupType.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/wakeup-types/${this.configuration.encodeParam({
        name: "type",
        value: type,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    updateWakeupType(type, wakeupType, observe = 'body', reportProgress = false, options) {
      if (type === null || type === undefined) {
        throw new Error('Required parameter type was null or undefined when calling updateWakeupType.');
      }
      if (wakeupType === null || wakeupType === undefined) {
        throw new Error('Required parameter wakeupType was null or undefined when calling updateWakeupType.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/wakeup-types/${this.configuration.encodeParam({
        name: "type",
        value: type,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: wakeupType,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function WakeupTypeWriteControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || WakeupTypeWriteControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftWriteConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: WakeupTypeWriteControllerService,
        factory: WakeupTypeWriteControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return WakeupTypeWriteControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const APIS = [AircraftSyncWriteControllerService, AircraftSystemWriteControllerService, AircraftWriteControllerService, CategoriesWriteControllerService, ConnectedCockpitRegistrationWriteControllerService, CustomerAircraftDeviceWriteControllerService, CustomerAircraftWriteControllerService, DataPrivacyWriteControllerService, DefaultAircraftImageWriteControllerService, EngineTypeWriteControllerService, FirewallAllowListWriteControllerService, FirewallFeatureWriteControllerService, FirewallServiceWriteControllerService, FixedDeviceWriteControllerService, LegacyModelWriteControllerService, MakeWriteControllerService, MasterAircraftCustomerRoleWriteControllerService, MasterAircraftInfoWriteControllerService, MasterAircraftSystemWriteControllerService, MasterAircraftTailNumberWriteControllerService, MasterAircraftWakeupSettingsWriteControllerService, MasterAircraftWriteControllerService, ModelWriteControllerService, OemAircraftModelWriteControllerService, OemUserWriteControllerService, OemWriteControllerService, RoleInviteWriteControllerService, SubcategoryWriteControllerService, WakeupTypeWriteControllerService];

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
let AircraftWriteApiModule = /*#__PURE__*/(() => {
  class AircraftWriteApiModule {
    static forRoot(configurationFactory) {
      return {
        ngModule: AircraftWriteApiModule,
        providers: [{
          provide: AircraftWriteConfiguration,
          useFactory: configurationFactory
        }]
      };
    }
    constructor(parentModule, http) {
      if (parentModule) {
        throw new Error('AircraftWriteApiModule is already loaded. Import in your base AppModule only.');
      }
      if (!http) {
        throw new Error('You need to import the HttpClientModule in your AppModule! \n' + 'See also https://github.com/angular/angular/issues/20575');
      }
    }
    static {
      this.ɵfac = function AircraftWriteApiModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftWriteApiModule)(i0.ɵɵinject(AircraftWriteApiModule, 12), i0.ɵɵinject(i1.HttpClient, 8));
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AircraftWriteApiModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return AircraftWriteApiModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AircraftWriteModule = /*#__PURE__*/(() => {
  class AircraftWriteModule {
    static {
      this.ɵfac = function AircraftWriteModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftWriteModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AircraftWriteModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [{
          ngModule: AircraftWriteApiModule,
          providers: [{
            provide: AircraftWriteConfiguration,
            deps: [FLY_WEB_COMMON_CONFIG],
            useFactory: config => new AircraftWriteConfiguration({
              basePath: `${AvcCoreDataBaseUrl[config.environment]}/aircraftManagementWriteService`
            })
          }]
        }]
      });
    }
  }
  return AircraftWriteModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { APIS, AircraftSyncWriteControllerService, AircraftSystemWriteControllerService, AircraftWriteApiModule, AircraftWriteConfiguration, AircraftWriteControllerService, AircraftWriteModule, BASE_PATH, COLLECTION_FORMATS, CategoriesWriteControllerService, ConnectedCockpitRegistrationWriteControllerService, CustomerAircraftDeviceWriteControllerService, CustomerAircraftWriteControllerService, DataPrivacyWriteControllerService, DefaultAircraftImageWriteControllerService, EngineTypeWriteControllerService, FirewallAllowListWriteControllerService, FirewallFeatureWriteControllerService, FirewallServiceWriteControllerService, FixedDeviceWriteControllerService, LegacyModelWriteControllerService, MakeWriteControllerService, MasterAircraftCustomerRoleWriteControllerService, MasterAircraftInfoWriteControllerService, MasterAircraftSystemWriteControllerService, MasterAircraftTailNumberWriteControllerService, MasterAircraftWakeupSettingsWriteControllerService, MasterAircraftWriteControllerService, ModelWriteControllerService, OemAircraftModelWriteControllerService, OemUserWriteControllerService, OemWriteControllerService, RoleInviteWriteControllerService, SubcategoryWriteControllerService, WakeupTypeWriteControllerService };
